import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Play } from 'lucide-react';
import {
  getSessionEvents,
  getSessions,
  qk,
  SESSION_FILTERS,
  SESSION_FILTER_LABELS,
  type SessionEventItem,
  type SessionFilter,
} from '../api.js';
import BilgiDugmesi from '../components/BilgiDugmesi.js';
import SegmentSecici, { type SegmentSecim } from '../components/SegmentSecici.js';
import DisaAktarDugmesi from '../components/DisaAktarDugmesi.js';
import { useRole } from '../components/AuthGate.js';
import Badge from '../components/Badge.js';
import Card from '../components/Card.js';
import EmptyState, { ErrorState } from '../components/EmptyState.js';
import ReplayPlayer from '../components/ReplayPlayer.js';
import Table from '../components/Table.js';
import { LoadingBlock } from '../components/Spinner.js';
import { useActivePid, useProjectContext } from '../components/Layout.js';
import { projectOrigin } from '../lib/project-origin.js';
import { formatDateTime, formatDuration, formatNumber, formatTL } from '../lib/format.js';

/**
 * §13.5 Oturumlar — filtreli liste (hata/rage/terk/dönüşüm) + olay zaman çizelgesi (replay v0).
 * Bulgular sayfasındaki "örnek oturumlar" linkleri `?sid=` ile buraya gelir.
 *
 * §22.7 — her satırda "Kaydı oynat" düğmesi vardır; oynatıcı sayfa düzenini hiç
 * bozmayan bir modal katmanda açılır (liste ve zaman çizelgesi olduğu gibi kalır).
 *
 * Görsel dil referans DXP admin panelindeki sayfa kabuğuyla birebir:
 * beyaz başlık şeridi → beyaz filtre şeridi → beyaz istatistik şeridi →
 * #fafafa zeminde px-6 py-8 içerik.
 */

const TYPE_ICONS: Record<string, string> = {
  page_view: '📄',
  page_leave: '🚪',
  click: '👆',
  rage_click: '😠',
  dead_click: '💀',
  scroll_depth: '📜',
  form_focus: '✏️',
  form_error: '⚠️',
  input_retry: '🔁',
  form_submit: '📨',
  js_error: '🐞',
  api_error: '🔌',
  web_vitals: '⚡',
  custom: '🏷️',
};

const DERIVED_LABELS: Record<string, string> = {
  product_view: 'ürün görüntüleme',
  add_to_cart: 'sepete ekleme',
  begin_checkout: 'ödemeye başlama',
  checkout_step: 'ödeme adımı',
  purchase: 'satın alma',
};

/** Filtre şeridi sekmesi — aktif olan marka tonunda (referans panelin sekme dili). */
function FilterTab({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`h-8 whitespace-nowrap rounded-lg px-3 text-[13px] transition-colors ${
        active
          ? 'bg-[#fff4ec] font-medium text-brand'
          : 'text-[#6b7280] hover:bg-[#f3f4f6] hover:text-[#111827]'
      }`}
    >
      {children}
    </button>
  );
}

/** §13.5 — her olay tipinin "önemli props" özeti. */
function summarizeProps(event: SessionEventItem): string {
  const p = event.props ?? {};
  const get = (k: string): string => (p[k] === undefined || p[k] === null ? '' : String(p[k]));
  switch (event.type) {
    case 'click':
    case 'dead_click':
      return get('selector');
    case 'rage_click':
      return `${get('selector')} · ${get('count')} tık`;
    case 'scroll_depth':
      return `%${get('pct')}`;
    case 'api_error':
      return `${get('endpoint_path')} → ${get('status')}`;
    case 'js_error':
      return get('message').slice(0, 120);
    case 'form_focus':
    case 'form_error':
    case 'input_retry':
      return `${get('form_id')}.${get('field')}`;
    case 'form_submit':
      return `${get('form_id')} · ${get('field_count')} alan`;
    case 'web_vitals':
      return ['lcp_ms', 'inp_ms', 'ttfb_ms']
        .filter((k) => p[k] !== undefined)
        .map((k) => `${k.replace('_ms', '').toUpperCase()} ${Math.round(Number(p[k]))}ms`)
        .join(' · ');
    case 'page_leave':
      return `${Math.round(Number(p['duration_ms'] ?? 0) / 1000)}s · scroll %${get('max_scroll_pct')}`;
    case 'page_view':
      return get('title');
    case 'custom':
      return get('name');
    default:
      return '';
  }
}

export default function Sessions() {
  const pid = useActivePid();
  const role = useRole(pid);
  /*
   * §22.4 — oynatıcıya `<base href>` olarak geçer; kayıttaki göreli adresler
   * panelin origin'ine değil müşterinin sitesine çözülsün diye. Domain KABUK
   * BAĞLAMINDAN okunur (§72.5/4 — ayrı bir `getProjects` çağrısı açılmaz).
   */
  const { projects } = useProjectContext();
  const baseOrigin = projectOrigin(projects.find((p) => p.id === pid)?.domain);
  const [searchParams, setSearchParams] = useSearchParams();
  const [filter, setFilter] = useState<SessionFilter | undefined>(undefined);
  /** §22.7 — oynatıcının açık olduğu oturum; null ise modal kapalıdır. */
  const [replaySid, setReplaySid] = useState<string | null>(null);
  /** Zemin tıklamasının GERÇEKTEN zeminde başlayıp başlamadığını tutar (aşağıda gerekçe). */
  const backdropPress = useRef(false);
  /** Modal paneli — açılışta odak buraya taşınır (aşağıda gerekçe). */
  const dialogRef = useRef<HTMLDivElement | null>(null);

  const selectedSid = searchParams.get('sid');

  /*
   * §92.4 — kayıtlı segment URL'de yaşar (`?segment=`); `?sid=` derin bağlantısıyla
   * aynı harita, çakışmazlar. Davranış filtresi (sekme) ile AND bileşir (§92.3).
   */
  const segmentId = searchParams.get('segment') ?? undefined;
  const segmentSecim: SegmentSecim =
    segmentId !== undefined ? { tur: 'kayitli', id: segmentId } : { tur: 'tumu' };

  function segmentDegistir(yeni: SegmentSecim): void {
    const next = new URLSearchParams(searchParams);
    if (yeni.tur === 'kayitli') next.set('segment', yeni.id);
    else next.delete('segment');
    setSearchParams(next, { replace: true });
  }

  const list = useQuery({
    queryKey: qk.sessions(pid, filter, segmentId),
    queryFn: ({ signal }) => getSessions(pid, filter, signal, segmentId),
  });

  const timeline = useQuery({
    queryKey: qk.sessionEvents(pid, selectedSid ?? ''),
    queryFn: ({ signal }) => getSessionEvents(pid, selectedSid as string, signal),
    enabled: Boolean(selectedSid),
  });

  const rows = list.data?.items ?? [];
  const events = timeline.data?.items ?? [];
  /*
   * §11.2 — "canlı" satırlar HENÜZ KAPANMAMIŞ oturumlardır (worker'ın Redis penceresi).
   * Ayrı sayılır çünkü süreleri/sayfa sayıları büyümeye devam eder: bu satırlarla
   * hesaplanan bir ortalama süre, kapanmış oturumlarınkiyle karşılaştırılabilir değildir.
   */
  const canli = rows.filter((r) => r.live === 1).length;
  /*
   * §33.5 — liste sunucu tavanına DEĞDİ mi? Sessiz kırpma, kullanıcının eksik bir
   * listeyi TAM sanmasına yol açar; sayı kararlarına (dönüşüm oranı, hata payı)
   * doğrudan yansır.
   */
  const kirpildi = list.data?.truncated === true;
  const firstTs = events[0]?.ts ?? 0;

  // İstatistik şeridi — yüklü filtrenin özeti (§13.5 tarama kolaylığı).
  const converted = rows.filter((r) => r.converted).length;
  const errored = rows.filter((r) => r.had_js_error || r.had_api_error || r.had_payment_error).length;
  const raged = rows.filter((r) => r.had_rage_click).length;

  // Modal davranışı: ESC kapatır, arkadaki uzun oturum listesi kaymaz (§22.7 oynatıcı
  // tam ekranda çalışır; kullanıcı yanlışlıkla listeyi kaydırırsa bağlamı kaybeder).
  useEffect(() => {
    if (replaySid === null) return;
    function onKeyDown(event: KeyboardEvent): void {
      if (event.key === 'Escape') setReplaySid(null);
    }
    const previousOverflow = document.body.style.overflow;
    const previouslyFocused = document.activeElement as HTMLElement | null;
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', onKeyDown);
    /*
     * `aria-modal` bir diyalogda odak diyaloğun İÇİNDE olmalıdır. Aksi hâlde odak,
     * örtünün ARKASINDA kalan "Kaydı oynat" düğmesinde kalır: Tab görünmeyen liste
     * kontrollerinde dolaşır ve ekran okuyucu diyaloğu hiç duyurmaz.
     * Oynatıcı kendi kontrolüne (ör. oynat düğmesi) odaklandıysa ona dokunmayız —
     * çocuk effect'i ebeveynden ÖNCE koştuğu için bu kontrol gereklidir.
     */
    if (dialogRef.current !== null && !dialogRef.current.contains(document.activeElement)) {
      dialogRef.current.focus();
    }
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
      // Kapanışta odak çağıran düğmeye döner (klavye kullanıcısı listede yerini kaybetmesin).
      previouslyFocused?.focus();
    };
  }, [replaySid]);

  function selectSession(sid: string | null): void {
    const next = new URLSearchParams(searchParams);
    if (sid) next.set('sid', sid);
    else next.delete('sid');
    setSearchParams(next, { replace: true });
  }

  /**
   * §22.7 — kaydı aç.
   *
   * KARAR (maliyet): Şartname "kayıt varsa (chunks.length > 0) düğme görünür" der; ancak
   * varlığı satır başına öğrenmek listedeki her oturum için ayrı bir `/replay` isteği
   * demektir (N+1). Liste yüzlerce satır olabildiğinden düğmeyi KOŞULSUZ gösteriyoruz:
   * tek istek ancak kullanıcı gerçekten tıkladığında, oynatıcının kendi içinde atılır.
   * Kayıt yoksa (örneklenmemiş/tetiklenmemiş oturum, §22.2) oynatıcı kendi boş durumunu
   * çizer — kullanıcı için sonuç aynı, sunucu için maliyet O(1).
   *
   * Kaydı açarken satırı da seçiyoruz: oynatıcının sağ panelindeki olay listesi (§22.7)
   * sayfanın zaten çektiği `timeline` verisidir; seçim hizalanmazsa ikinci bir events
   * isteği gerekirdi.
   */
  function openReplay(sid: string): void {
    if (sid !== selectedSid) selectSession(sid);
    setReplaySid(sid);
  }

  return (
    <div className="min-h-full bg-[#fafafa]" data-testid="oturumlar-sayfasi">
      {/* başlık */}
      <div className="flex items-center justify-between border-b border-[#e5e7eb] bg-white px-6 py-4">
        <div className="flex items-center gap-2">
          <h1 className="font-grotesk text-[18px] font-bold text-[#111827]">Oturumlar</h1>
          <BilgiDugmesi testId="oturumlar-bilgi">Satıra tıklayın — olay zaman çizelgesi açılır (replay v0). Filtreler §10 `?filter=` sözleşmesine karşılık gelir.</BilgiDugmesi>
          <span className="ml-1 hidden text-[13px] text-[#9ca3af] md:inline">
            Satıra tıklayın — olay zaman çizelgesi açılır
          </span>
        </div>
      </div>

      {/* filtreler */}
      <nav
        aria-label="Oturum filtresi"
        className="flex flex-wrap items-center gap-1 border-b border-[#e5e7eb] bg-white px-6 py-3"
      >
        <FilterTab active={filter === undefined} onClick={() => setFilter(undefined)}>
          Tümü
        </FilterTab>
        {SESSION_FILTERS.map((f) => (
          <FilterTab key={f} active={filter === f} onClick={() => setFilter(f)}>
            {SESSION_FILTER_LABELS[f]}
          </FilterTab>
        ))}
        <span className="mx-2 h-4 w-px bg-[#e5e7eb]" aria-hidden />
        <SegmentSecici pid={pid} role={role} secim={segmentSecim} onChange={segmentDegistir} />
        <span className="ml-auto">
          {/* §95.1 — aktif sekme + segment export'a aynen gider ("gördüğünü indir"). */}
          <DisaAktarDugmesi
            pid={pid}
            role={role}
            dataset="sessions"
            params={{ filter, segment: segmentId }}
          />
        </span>
      </nav>

      {/* istatistikler */}
      <div className="flex flex-wrap items-center gap-8 border-b border-[#e5e7eb] bg-white px-6 py-2.5">
        {list.isLoading ? (
          <span className="animate-pulse text-[12px] text-[#9ca3af]">Veri yükleniyor…</span>
        ) : (
          [
            { label: 'Oturum', value: formatNumber(rows.length) },
            { label: 'Devam eden', value: formatNumber(canli) },
            { label: 'Dönüşen', value: formatNumber(converted) },
            { label: 'Hatalı', value: formatNumber(errored) },
            { label: 'Rage click', value: formatNumber(raged) },
          ].map((s) => (
            <div key={s.label} className="flex items-center gap-2">
              <span className="text-[12px] text-[#9ca3af]">{s.label}</span>
              <span className="num text-[13px] font-semibold text-[#111827]">{s.value}</span>
            </div>
          ))
        )}

        {/*
          §33.5 — kırpma AÇIKÇA söylenir. Bu rozet olmadan yukarıdaki "Oturum: 200"
          sayısı, gerçekte 12.000 oturum varken de aynı görünür ve okuyan kişi
          yüzdeleri TÜM trafiğe ait sanır.
        */}
        {!list.isLoading && kirpildi ? (
          <span
            data-testid="oturum-listesi-kirpildi"
            title={`Sunucu bu uçta en çok ${String(list.data?.limit ?? 0)} satır döndürür.`}
            className="rounded-md border border-[#fde68a] bg-[#fffbeb] px-2 py-0.5 text-[11px] font-medium text-[#92400e]"
          >
            İlk {formatNumber(list.data?.limit ?? 0)} oturum gösteriliyor
          </span>
        ) : null}
      </div>

      {/* içerik */}
      <div className="space-y-6 px-6 py-8">
        {list.isError ? (
          <ErrorState
            title="Oturumlar alınamadı"
            description={(list.error as Error).message}
            onRetry={() => void list.refetch()}
          />
        ) : null}

        <Card flush>
          <Table
            loading={list.isLoading}
            rows={rows}
            rowKey={(r) => r.session_id}
            onRowClick={(r) => selectSession(r.session_id === selectedSid ? null : r.session_id)}
            isRowActive={(r) => r.session_id === selectedSid}
            stickyHeader
            emptyTitle="Bu filtreye uyan oturum yok"
            emptyDescription="Devam eden oturumlar da bu listede görünür — boşsa siteye henüz (bot olmayan) trafik gelmemiş demektir. Etiketi Ayarlar'dan doğrulayın."
            columns={[
              {
                key: 'session_id',
                header: 'Oturum',
                render: (r) => (
                  <span className="flex items-center gap-2">
                    <span className="font-mono text-[12px] text-[#111827]">
                      {r.session_id.slice(0, 10)}
                    </span>
                    {/*
                      §11.2 — oturum hâlâ açık. Rozet olmadan bu satır kapanmış bir
                      oturumdan ayırt edilemezdi ve "Süre 12sn" değeri NİHAİ sanılırdı.
                    */}
                    {r.live === 1 ? (
                      <span
                        data-testid="canli-oturum-rozeti"
                        title="Oturum devam ediyor — süre ve sayfa sayısı artmaya devam eder. 30 dk hareketsizlikten sonra kapanır ve kalıcı olarak yazılır."
                        className="inline-flex items-center gap-1 rounded-md border border-emerald-200 bg-emerald-50 px-1.5 py-0.5 text-[10px] font-medium text-emerald-700"
                      >
                        <span
                          aria-hidden="true"
                          className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-500"
                        />
                        canlı
                      </span>
                    ) : null}
                  </span>
                ),
              },
              { key: 'started_at', header: 'Başlangıç', render: (r) => formatDateTime(r.started_at) },
              { key: 'duration_ms', header: 'Süre', render: (r) => formatDuration(r.duration_ms) },
              { key: 'device', header: 'Cihaz' },
              { key: 'channel', header: 'Kanal' },
              {
                key: 'page_count',
                header: 'Sayfa',
                align: 'right',
                render: (r) => formatNumber(r.page_count),
              },
              {
                key: 'durum',
                header: 'Durum',
                render: (r) => (
                  <div className="flex flex-wrap gap-1">
                    {r.converted ? (
                      <Badge tone="success" size="sm">
                        dönüşüm {r.revenue ? formatTL(r.revenue) : ''}
                      </Badge>
                    ) : null}
                    {r.had_payment_error ? (
                      <Badge tone="danger" size="sm">
                        ödeme hatası
                      </Badge>
                    ) : null}
                    {r.had_js_error || r.had_api_error ? (
                      <Badge tone="warning" size="sm">
                        hata
                      </Badge>
                    ) : null}
                    {r.had_rage_click ? (
                      <Badge tone="accent" size="sm">
                        rage
                      </Badge>
                    ) : null}
                  </div>
                ),
              },
              {
                key: 'kayit',
                header: 'Kayıt',
                align: 'right',
                render: (r) => (
                  <button
                    type="button"
                    data-testid="kaydi-oynat"
                    // testid her satırda tekrar eder; §22.8 M9 e2e'si BELİRLİ bir oturumu
                    // (mobile_frustrated) açmak zorunda olduğu için ayırt edici bir kanca
                    // gerekir — `[data-testid="kaydi-oynat"][data-sid="…"]`.
                    data-sid={r.session_id}
                    className="btn-ghost"
                    title="Bu oturumun ekran kaydını oynat"
                    // Satır tıklaması zaman çizelgesini aç/kapat yapıyor; düğme kendi
                    // eylemini üstlendiği için olayı satıra taşımıyoruz.
                    onClick={(event) => {
                      event.stopPropagation();
                      openReplay(r.session_id);
                    }}
                    // Table satırı Enter/Space'i yakalayıp preventDefault ediyor
                    // (Table.tsx erişilebilirlik davranışı). Olayı burada durdurmazsak
                    // düğme klavyeyle hiç çalışmaz — tıklama olayı üretilmeden iptal edilir.
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') event.stopPropagation();
                    }}
                  >
                    <Play aria-hidden="true" className="h-3.5 w-3.5 text-[#9ca3af]" />
                    Kaydı oynat
                  </button>
                ),
              },
            ]}
          />
        </Card>

        {selectedSid ? (
          <Card
            title="Olay zaman çizelgesi"
            /*
             * §33.5 — olay listesi de zarflıdır (`SESSION_EVENTS_LIMIT`). Panel yalnız
             * `items`i okuyup "N olay" yazıyordu: tavana değen uzun bir oturumda
             * çizelgenin SONU kesiliyor, kullanıcı oturumun orada bittiğini sanıyordu
             * (denetim bulgusu B5). Kırpılma başlıkta söylenir.
             */
            subtitle={
              timeline.data?.truncated === true
                ? `Oturum ${selectedSid.slice(0, 12)} · ilk ${formatNumber(timeline.data.limit)} olay gösteriliyor (çizelge kırpıldı)`
                : `Oturum ${selectedSid.slice(0, 12)} · ${events.length} olay`
            }
            actions={
              <button type="button" className="btn-ghost" onClick={() => selectSession(null)}>
                Kapat
              </button>
            }
          >
            {timeline.isLoading ? <LoadingBlock label="Olaylar yükleniyor…" /> : null}
            {!timeline.isLoading && events.length === 0 ? (
              <EmptyState compact title="Bu oturumda olay bulunamadı" />
            ) : null}

            <ol className="space-y-0.5" data-testid="olay-zaman-cizelgesi">
              {events.map((event, index) => (
                <li
                  key={`${event.seq}-${index}`}
                  className="flex items-start gap-3 rounded px-2 py-1.5 transition-colors hover:bg-[#fafafa]"
                >
                  <span className="w-14 shrink-0 text-right font-mono text-[11px] leading-5 text-[#9ca3af]">
                    +{((event.ts - firstTs) / 1000).toFixed(1)}s
                  </span>
                  <span className="shrink-0 text-[13px] leading-5" aria-hidden>
                    {TYPE_ICONS[event.type] ?? '•'}
                  </span>
                  <span className="w-32 shrink-0 text-[13px] font-medium leading-5 text-[#374151]">
                    {event.type}
                  </span>
                  <span className="w-48 shrink-0 truncate font-mono text-[11px] leading-5 text-[#9ca3af]">
                    {event.url_path}
                  </span>
                  <span className="min-w-0 flex-1 truncate text-[12px] leading-5 text-[#6b7280]">
                    {summarizeProps(event)}
                  </span>
                  {event.derived_type ? (
                    <Badge tone="accent" size="sm">
                      {DERIVED_LABELS[event.derived_type] ?? event.derived_type}
                    </Badge>
                  ) : null}
                </li>
              ))}
            </ol>
          </Card>
        ) : null}
      </div>

      {/*
        §22.7 oynatıcı — modal katman. Sayfa akışına yerleştirmek yerine `fixed` katman
        seçildi: mevcut liste/zaman çizelgesi düzeni (ve onları sorgulayan e2e testleri)
        hiç değişmiyor, oynatıcı ise iframe + olay paneli için gereken geniş alanı buluyor.
      */}
      {replaySid !== null ? (
        <div
          role="presentation"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          // Zemine tıklayınca kapanır. Ancak oynatıcının zaman çizelgesinde sürüklerken
          // imleç modalın dışına taşabildiği için, yalnızca basma DA zeminde başladıysa
          // kapatıyoruz — aksi hâlde scrub sonunda oynatıcı kendiliğinden kapanırdı.
          onMouseDown={(event) => {
            backdropPress.current = event.target === event.currentTarget;
          }}
          onClick={(event) => {
            if (event.target === event.currentTarget && backdropPress.current) setReplaySid(null);
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Oturum kaydı oynatıcı"
            data-testid="kayit-oynatici"
            ref={dialogRef}
            tabIndex={-1}
            className="flex h-[min(820px,92vh)] w-[min(1200px,95vw)] flex-col overflow-hidden rounded-2xl border border-[#e5e7eb] bg-white shadow-xl focus:outline-none"
          >
            {/* Oynatıcının iç yüksekliği ne olursa olsun modalı taşırmasın. */}
            <div className="flex min-h-0 flex-1 flex-col overflow-auto">
              <ReplayPlayer
                pid={pid}
                sid={replaySid}
                // Sağ paneldeki olay listesi: sayfanın zaten çektiği veri. Seçim henüz
                // bu oturuma geçmediyse (aynı render karesi) yanlış oturumun olaylarını
                // göstermemek için boş geçiyoruz.
                //
                // BİRİM UYARISI (§22.4): buradaki `ts` MUTLAK epoch ms'dir
                // (`EnrichedEvent.ts`, §10 events ucu) — oturum-rölatif DEĞİL.
                // Replay olaylarının `ts`'i ise chunk'a rölatiftir; oynatma zamanıyla
                // karşılaştırma ancak `chunk.started_at + replayEvent.ts` üzerinden
                // yapılırsa doğru olur.
                sessionEvents={replaySid === selectedSid ? events : []}
                baseOrigin={baseOrigin}
                onClose={() => setReplaySid(null)}
              />
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
